import { useCallback, useEffect, useState, type CSSProperties } from 'react';

import PageHeader from '../../components/layout/PageHeader';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { useMovablePopup } from '../../components/MovablePopup';
import { readerLocale } from '../../i18n/language';
import contractTemplatesService, {
  type ContractTemplate,
  type ContractTemplateVersion,
} from '../../services/contractTemplates.service';
import productAccessService, { type ProductAccess } from '../../services/productAccess.service';
import {
  formatBytes,
  isPublishable,
  offerStateLabel,
  productsWithoutTemplate,
  scopeLabel,
  versionStatus,
} from './contractTemplates.helpers';
import { NewTemplatePanel, UploadVersionPanel } from './ContractTemplateForms';

/**
 * Super Admin — Contract template manager (GitLab gap: no UI existed; contract
 * templates were DB/seed-managed only, so a contract couldn't be made
 * available to clients from the back office).
 *
 * A contract is offered to clients only when its template is ACTIVE and has a
 * PUBLISHED version (the highest published version is the live one). Publishing
 * is permanent — published versions are immutable (write once); to replace
 * the live one, publish a higher version; to withdraw a contract, deactivate
 * its template. A super admin also creates a product's template and uploads its versions here
 * (#277): a PDF, or the generated placeholder agreement. Outside production every contract a client opens carries the DRAFT mark.
 */

type Pending =
  | { kind: 'activate' | 'deactivate'; template: ContractTemplate }
  | { kind: 'publish'; template: ContractTemplate; version: ContractTemplateVersion };

function modalTitle(p: Pending): string {
  if (p.kind === 'publish') return `Publish v${p.version.version} — ${p.template.title}`;
  return `${p.kind === 'activate' ? 'Activate' : 'Deactivate'} — ${p.template.title}`;
}

function modalHint(p: Pending): string {
  if (p.kind === 'publish') {
    return 'Publishing is permanent: a published version can never be unpublished or edited. To replace it later, publish a higher version number.';
  }
  if (p.kind === 'deactivate') {
    return 'Clients will no longer be offered this contract. Published versions stay on record.';
  }
  return 'Once it also has a published version, clients can be offered this contract.';
}

// ── Small presentational pieces ───────────────────────────────────────
const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  live: { bg: colors.successBg, fg: colors.success, label: 'LIVE' },
  superseded: { bg: colors.canvas, fg: colors.muted, label: 'PUBLISHED' },
  draft: { bg: colors.canvas, fg: colors.inkSoft, label: 'DRAFT' },
};

function Badge({ bg, fg, label }: Readonly<{ bg: string; fg: string; label: string }>): JSX.Element {
  return (
    <span
      style={{
        background: bg,
        color: fg,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        padding: '2px 8px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

const btn: CSSProperties = {
  padding: '6px 12px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  border: `1px solid ${colors.border}`,
  background: colors.surface,
  color: colors.ink,
};

function VersionRow({
  version,
  liveVersion,
  onPublish,
}: Readonly<{
  version: ContractTemplateVersion;
  liveVersion: number | null;
  onPublish: () => void;
}>): JSX.Element {
  const status = versionStatus(version, liveVersion);
  const sty = STATUS_STYLE[status];
  const publishable = isPublishable(version, liveVersion);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderTop: `1px solid ${colors.borderSoft}`,
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 700, color: colors.ink, width: 44 }}>v{version.version}</span>
      <Badge bg={sty.bg} fg={sty.fg} label={sty.label} />
      <span style={{ color: colors.muted, flex: 1, minWidth: 0 }}>
        {version.is_published && version.published_at
          ? `Published ${new Date(version.published_at).toLocaleDateString(readerLocale())}`
          : `Created ${new Date(version.created_at).toLocaleDateString(readerLocale())}`}
        {' · '}
        {formatBytes(version.source_size_bytes)}
        {version.expires_after_days ? ` · expires ${version.expires_after_days}d` : ''}
      </span>
      {version.is_published ? (
        <span style={{ fontSize: 11, color: colors.muted }}>immutable</span>
      ) : (
        <button
          type="button"
          onClick={onPublish}
          disabled={!publishable}
          title={publishable ? 'Publish this version' : 'Publish a newer version than the live one'}
          style={{
            ...btn,
            background: publishable ? colors.navy : colors.canvas,
            color: publishable ? '#fff' : colors.mutedSoft,
            border: 'none',
            cursor: publishable ? 'pointer' : 'not-allowed',
          }}
        >
          Publish
        </button>
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onSetActive,
  onPublish,
  onUpdated,
}: Readonly<{
  template: ContractTemplate;
  onSetActive: (next: boolean) => void;
  onPublish: (v: ContractTemplateVersion) => void;
  onUpdated: (t: ContractTemplate) => void;
}>): JSX.Element {
  const offerable = template.is_offerable;
  const [uploading, setUploading] = useState(false);
  return (
    <section
      style={{
        background: colors.surface,
        border: `1px solid ${offerable ? colors.successBorder : colors.border}`,
        borderRadius: 14,
        padding: 18,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: colors.ink, margin: 0 }}>
              {template.title}
            </h2>
            <Badge
              bg={offerable ? colors.successBg : colors.canvas}
              fg={offerable ? colors.success : colors.muted}
              label={offerStateLabel(template)}
            />
            <Badge
              bg={template.is_active ? colors.successBg : colors.dangerBg}
              fg={template.is_active ? colors.success : colors.danger}
              label={template.is_active ? 'ACTIVE' : 'INACTIVE'}
            />
          </div>
          <p style={{ fontSize: 12, color: colors.muted, margin: '4px 0 0' }}>
            {scopeLabel(template)} · <span style={{ fontFamily: 'monospace' }}>{template.slug}</span>
          </p>
          {template.description && (
            <p style={{ fontSize: 13, color: colors.inkSoft, margin: '8px 0 0' }}>
              {template.description}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => onSetActive(!template.is_active)}
          style={{
            ...btn,
            borderColor: template.is_active ? colors.dangerBorder : colors.successBorder,
            color: template.is_active ? colors.danger : colors.success,
          }}
        >
          {template.is_active ? 'Deactivate' : 'Activate'}
        </button>
      </div>

      <div style={{ marginTop: 12 }}>
        {template.versions.length === 0 ? (
          <p style={{ fontSize: 12, color: colors.mutedSoft, fontStyle: 'italic' }}>
            No versions uploaded yet.
          </p>
        ) : (
          template.versions.map((v) => (
            <VersionRow
              key={v.id}
              version={v}
              liveVersion={template.live_version}
              onPublish={() => onPublish(v)}
            />
          ))
        )}
        {uploading ? (
          <UploadVersionPanel
            template={template}
            onClose={() => setUploading(false)}
            onUploaded={(t) => {
              setUploading(false);
              onUpdated(t);
            }}
          />
        ) : (
          <button type="button" onClick={() => setUploading(true)} style={{ ...btn, marginTop: 10 }}>
            Upload a version
          </button>
        )}
      </div>
    </section>
  );
}

function ConfirmModal({
  pending,
  onClose,
  onConfirm,
}: Readonly<{
  pending: Pending;
  onClose: () => void;
  onConfirm: (reason: string, password: string) => Promise<void>;
}>): JSX.Element {
  const popup = useMovablePopup<HTMLDivElement>();
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm(reason.trim(), password);
      // On success the parent unmounts this modal.
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Action failed.'));
      setSubmitting(false);
    }
  };

  const canSubmit = reason.trim().length >= 4 && !submitting;

  return (
    <dialog
      open
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        margin: 0,
        maxWidth: 'none',
        maxHeight: 'none',
        width: '100%',
        height: '100%',
        border: 'none',
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
    >
      <div
        ref={popup.panelRef}
        style={{
          ...popup.panelStyle,
          background: colors.surface,
          borderRadius: 14,
          padding: 22,
          width: 'min(480px, 100%)',
          border: `1px solid ${colors.border}`,
        }}
      >
        {popup.bar}
        <h3 style={{ fontSize: 16, fontWeight: 700, color: colors.ink, margin: 0 }}>
          {modalTitle(pending)}
        </h3>
        <p style={{ fontSize: 13, color: colors.muted, margin: '8px 0 14px' }}>
          {modalHint(pending)}
        </p>

        {err && (
          <div
            role="alert"
            style={{
              background: colors.dangerBg,
              color: colors.danger,
              border: `1px solid ${colors.dangerBorder}`,
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            {err}
          </div>
        )}

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.inkSoft }}>
          Reason (audited, min 4 chars){/* */}
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            style={{
              width: '100%',
              marginTop: 4,
              padding: 8,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
              resize: 'vertical',
            }}
          />
        </label>

        <label
          style={{ display: 'block', fontSize: 12, fontWeight: 600, color: colors.inkSoft, marginTop: 12 }}
        >
          Your password (local accounts only){/* */}
          <input
            type="password"
            value={password}
            autoComplete="off"
            onChange={(e) => setPassword(e.target.value)}
            style={{
              width: '100%',
              marginTop: 4,
              padding: 8,
              borderRadius: 8,
              border: `1px solid ${colors.border}`,
              fontSize: 13,
            }}
          />
        </label>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button type="button" onClick={onClose} disabled={submitting} style={btn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            style={{
              ...btn,
              background: canSubmit ? colors.navy : colors.canvas,
              color: canSubmit ? '#fff' : colors.mutedSoft,
              border: 'none',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {submitting ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

const PAGE_TITLE = 'Contract templates';
const PAGE_SUBTITLE =
  'Activate a template and publish a version to make a contract available to clients. Published versions are immutable.';

export default function ContractTemplates(): JSX.Element {
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [products, setProducts] = useState<ProductAccess[]>([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [data, productRows] = await Promise.all([
        contractTemplatesService.list(),
        productAccessService.list(),
      ]);
      setTemplates(data);
      setProducts(productRows);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not load contract templates.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const applyUpdate = useCallback((updated: ContractTemplate) => {
    setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
  }, []);

  const confirm = useCallback(
    async (reason: string, password: string): Promise<void> => {
      if (!pending) return;
      const pw = password.trim() === '' ? undefined : password;
      const updated =
        pending.kind === 'publish'
          ? await contractTemplatesService.publishVersion(
              pending.template.id,
              pending.version.id,
              { reason, actor_password: pw },
            )
          : await contractTemplatesService.setActive(pending.template.id, {
              is_active: pending.kind === 'activate',
              reason,
              actor_password: pw,
            });
      applyUpdate(updated);
      setPending(null);
    },
    [pending, applyUpdate],
  );

  return (
    <>
      <PageHeader title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />

      {loading && <p style={{ fontSize: 14, color: colors.muted }}>Loading…</p>}

      {!loading && error && (
        <div
          role="alert"
          style={{
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
            borderRadius: 10,
            padding: 14,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && (
        <NewTemplatePanel
          products={productsWithoutTemplate(products, templates)}
          onCreated={(t) => setTemplates((prev) => [t, ...prev])}
        />
      )}

      {!loading && !error && templates.length === 0 && (
        <div
          style={{
            background: colors.surface,
            border: `1px solid ${colors.border}`,
            borderRadius: 12,
            padding: 20,
            fontSize: 14,
            color: colors.muted,
          }}
        >
          No contract templates exist yet.
        </div>
      )}

      {!loading &&
        !error &&
        templates.map((t) => (
          <TemplateCard
            key={t.id}
            template={t}
            onSetActive={(next) =>
              setPending({ kind: next ? 'activate' : 'deactivate', template: t })
            }
            onPublish={(v) => setPending({ kind: 'publish', template: t, version: v })}
            onUpdated={applyUpdate}
          />
        ))}

      {pending && (
        <ConfirmModal pending={pending} onClose={() => setPending(null)} onConfirm={confirm} />
      )}
    </>
  );
}
